import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface TaskPollingControlContextValue {
  isDiscoveryPaused: boolean
  acquireDiscoveryPause: () => () => void
}

const TaskPollingControlContext = createContext<TaskPollingControlContextValue | undefined>(undefined)

export const TaskPollingControlProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [discoveryPauseCount, setDiscoveryPauseCount] = useState(0)

  const acquireDiscoveryPause = useCallback(() => {
    setDiscoveryPauseCount((count) => count + 1)

    let released = false
    return () => {
      if (released) return
      released = true
      setDiscoveryPauseCount((count) => Math.max(0, count - 1))
    }
  }, [])

  const value = useMemo(
    () => ({
      isDiscoveryPaused: discoveryPauseCount > 0,
      acquireDiscoveryPause,
    }),
    [acquireDiscoveryPause, discoveryPauseCount]
  )

  return (
    <TaskPollingControlContext.Provider value={value}>
      {children}
    </TaskPollingControlContext.Provider>
  )
}

export function useTaskPollingControl(): TaskPollingControlContextValue {
  const context = useContext(TaskPollingControlContext)
  if (!context) {
    throw new Error('useTaskPollingControl must be used within a TaskPollingControlProvider')
  }
  return context
}

export function usePauseTaskDiscovery(enabled = true): void {
  const { acquireDiscoveryPause } = useTaskPollingControl()

  useEffect(() => {
    if (!enabled) return
    return acquireDiscoveryPause()
  }, [acquireDiscoveryPause, enabled])
}
